import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatErr } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = logged-in
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    // Returning from Google OAuth: AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) {
      setUser(false);
      setLoading(false);
      return;
    }
    localStorage.removeItem("obligo_token"); // purge token left by the pre-cookie version
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "string" && d.startsWith("Office hours")) sessionStorage.setItem("obligo_logout_reason", `${d} Your running timer was stopped at the end time.`);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Returns { ok, pending } — pending is set when email verification or a 2-step code is still needed.
  const authPost = useCallback(async (url, body) => {
    try {
      const { data } = await api.post(url, body);
      if (data.user) {
        setUser(data.user);
        api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
        return { ok: true };
      }
      return { ok: true, pending: data };
    } catch (e) {
      return { ok: false, error: formatErr(e) };
    }
  }, []);

  const login = useCallback((email, password) => authPost("/auth/login", { email, password }), [authPost]);
  const register = useCallback((name, email, password) => authPost("/auth/register", { name, email, password }), [authPost]);

  const setSession = useCallback((data) => {
    setUser(data.user);
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/activity/segment/signoff"); } catch (e) { console.warn("Sign-off failed:", e); }
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout failed:", e); }
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) { console.warn("Refreshing user failed:", e); }
  }, []);

  const value = useMemo(() => ({ user, loading, login, register, logout, refreshUser, setSession }),
    [user, loading, login, register, logout, refreshUser, setSession]);

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
